import { useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import { isLaborMappingRowComplete, validateLaborMappingRows } from "../utils/invProfileLaborMapping";

/**
 * One labor mapping row (company #, jacket) for LABOR_HOUR invoice profile services.
 */
export function LaborMappingRowEditor({
  row,
  rowIndex,
  rowCount,
  rowErrors,
  assignedCompanyNumbers,
  jacketOptions,
  isJacketsLoading,
  isJacketsError,
  fieldClassName,
  onRowPatch,
  onAddRow,
  onRemoveLastRow,
  allRows,
}) {
  const companyNumber = String(row.companyNumber ?? "").trim();
  const jobNo = String(row.jobNo ?? "").trim();
  const singleCompany = assignedCompanyNumbers.length === 1;
  const companyDisplay = singleCompany ? assignedCompanyNumbers[0] : companyNumber;
  const singleJacket =
    !isJacketsLoading && !isJacketsError && jacketOptions.length === 1
      ? String(jacketOptions[0] ?? "").trim()
      : "";
  const jacketDisplay = singleJacket || jobNo;
  const isLastRow = rowIndex === rowCount - 1;

  useEffect(() => {
    if (!singleJacket || jobNo === singleJacket) {
      return;
    }
    onRowPatch({ jobNo: singleJacket });
  }, [singleJacket, jobNo, onRowPatch]);

  const rowIsComplete = isLaborMappingRowComplete(row, assignedCompanyNumbers);
  const rowErrorsList = validateLaborMappingRows(allRows ?? [row], assignedCompanyNumbers);
  const rowHasValidationErrors = Boolean(rowErrorsList?.[rowIndex]);
  const canAddAnotherRow = isLastRow && rowIsComplete && !rowHasValidationErrors;

  return (
    <div
      className={
        rowIndex < rowCount - 1 ? "pb-4 border-b border-gray-200 dark:border-gray-700" : ""
      }
    >
      {rowCount > 1 ? (
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">
          Labor Mapping {rowIndex + 1}
        </p>
      ) : null}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Company Number *
          </label>
          {assignedCompanyNumbers.length === 0 ? (
            <input
              type="text"
              readOnly
              value=""
              placeholder="No company number assigned"
              aria-readonly="true"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
            />
          ) : singleCompany ? (
            <input
              type="text"
              readOnly
              value={companyDisplay}
              aria-readonly="true"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
            />
          ) : (
            <select
              value={companyNumber}
              onChange={(e) => onRowPatch({ companyNumber: e.target.value })}
              aria-invalid={Boolean(rowErrors?.companyNumber)}
              className={fieldClassName(Boolean(rowErrors?.companyNumber))}
            >
              <option value="">Select company number</option>
              {assignedCompanyNumbers.map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </select>
          )}
          {rowErrors?.companyNumber ? (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
              {rowErrors.companyNumber}
            </p>
          ) : null}
          {isLastRow ? (
            <button
              type="button"
              disabled={!canAddAnotherRow}
              onClick={() => {
                if (!canAddAnotherRow) {
                  return;
                }
                onAddRow();
              }}
              className={`app-btn-gloss mt-2 px-2 py-1 text-xs ${
                !canAddAnotherRow ? "cursor-not-allowed opacity-50" : ""
              }`}
              title={
                !canAddAnotherRow
                  ? "Complete this labor mapping before adding another"
                  : "Add another labor mapping"
              }
              aria-label={`Add labor mapping after labor mapping ${rowIndex + 1}`}
              aria-disabled={!canAddAnotherRow}
            >
              <Plus className="w-3.5 h-3.5" />
              Add labor mapping
            </button>
          ) : null}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Jackets *
          </label>
          {isJacketsLoading ? (
            <input
              type="text"
              readOnly
              value="Loading…"
              aria-readonly="true"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
            />
          ) : isJacketsError ? (
            <input
              type="text"
              readOnly
              value=""
              placeholder="Unable to load jackets"
              aria-readonly="true"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
            />
          ) : jacketOptions.length === 0 ? (
            <input
              type="text"
              readOnly
              value=""
              placeholder="No jackets assigned"
              aria-readonly="true"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
            />
          ) : singleJacket ? (
            <input
              type="text"
              readOnly
              value={jacketDisplay}
              aria-readonly="true"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 cursor-not-allowed"
            />
          ) : (
            <select
              value={jobNo}
              onChange={(e) => onRowPatch({ jobNo: e.target.value })}
              aria-invalid={Boolean(rowErrors?.jobNo)}
              className={fieldClassName(Boolean(rowErrors?.jobNo))}
            >
              <option value="">Select jacket</option>
              {jacketOptions.map((optionJobNo) => (
                <option key={optionJobNo} value={optionJobNo}>
                  {optionJobNo}
                </option>
              ))}
            </select>
          )}
          {isJacketsError ? (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
              Unable to load jackets for this invoice profile.
            </p>
          ) : null}
          {!isJacketsLoading && !isJacketsError && jacketOptions.length === 0 ? (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              No jackets are assigned to this invoice profile.
            </p>
          ) : null}
          {rowErrors?.jobNo ? (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
              {rowErrors.jobNo}
            </p>
          ) : null}
        </div>
      </div>
      {rowIndex === rowCount - 1 && rowCount > 1 ? (
        <div className="mt-3">
          <button
            type="button"
            onClick={onRemoveLastRow}
            className="inline-flex items-center gap-1 rounded border border-red-300 dark:border-red-800 px-2 py-1 text-xs font-medium text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
            title="Remove last labor mapping"
            aria-label="Remove last labor mapping"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Remove
          </button>
        </div>
      ) : null}
    </div>
  );
}
